import type { CompanyLogoDialogProps, LogoDialogImage } from '@/components/company-logo-dialog';
import { companiesApi } from '@/lib/api';
import { sameLogoFraming } from '@/lib/logo-framing';
import { NEUTRAL_LOGO_FRAMING } from '@mss_creator/shared';
import { useQueryClient } from '@tanstack/react-query';

/** The last segment of a URL's path, as the name an image fetched from it goes by. */
export function fileNameOfUrl(url: string): string {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : 'logo';
  } catch {
    return 'logo';
  }
}

/** « récupéré depuis exemple.fr », the line under the previews of an image fetched from a URL. */
export function urlDetail(url: string): string {
  try {
    return `récupéré depuis ${new URL(url).hostname}`;
  } catch {
    return 'récupéré depuis une adresse';
  }
}

/**
 * The logo editor's three actions for a company that exists (the settings
 * page): each one is saved on the server at once, and the company's queries are
 * refreshed so the grid and the settings show the new logo. « Récupérer » stores
 * the downloaded image already — the editor then only frames it.
 */
export function useStoredCompanyLogo(
  companyId: string,
): Pick<CompanyLogoDialogProps, 'onImportUrl' | 'onSave' | 'onRemove'> {
  const queryClient = useQueryClient();

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['company', companyId] });
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
  }

  return {
    onImportUrl: async (url): Promise<LogoDialogImage> => {
      const company = await companiesApi.importLogoFromUrl(companyId, url);
      refresh();
      if (!company.logo) {
        throw new Error('The server stored a logo but did not return it.');
      }
      return {
        src: company.logo.src,
        file: null,
        name: fileNameOfUrl(url),
        detail: urlDetail(url),
      };
    },
    onSave: async ({ file, framing }) => {
      try {
        if (file) {
          await companiesApi.uploadLogo(companyId, file);
        }
        // A new image starts neutral on the server: only a framing that differs is worth a call.
        if (file === null || !sameLogoFraming(framing, NEUTRAL_LOGO_FRAMING)) {
          await companiesApi.saveLogoFraming(companyId, framing);
        }
      } finally {
        // The image may be stored even when its framing was not: show what is there.
        refresh();
      }
    },
    onRemove: async () => {
      await companiesApi.removeLogo(companyId);
      refresh();
    },
  };
}
